import { useCallback, useRef, useState } from 'react';
import FixedAspectCropModal from '@/components/image/FixedAspectCropModal';
import {
  PRODUCT_CAROUSEL_ASPECT,
  PRODUCT_CAROUSEL_ASPECT_LABEL,
  readImageNaturalSize,
} from '@/components/image/fixedAspectCrop';
import {
  canCropToMinEdge,
  needsFixedAspectCrop,
} from '@/components/image/fixedAspectCropGatePolicy';

/**
 * 上传前按固定比例门禁：比例或最小输出尺寸不合规则弹裁剪框，用户确认后继续。
 */
export function useFixedAspectCropGate({
  aspect = PRODUCT_CAROUSEL_ASPECT,
  aspectLabel = PRODUCT_CAROUSEL_ASPECT_LABEL,
  title = '裁剪为平台要求比例',
  hint = '',
  outputWidth,
  outputHeight,
  maxEdge,
  maxBytes,
  /** 裁剪源区域最短边下限；过小无法裁出合格区时直接报错 */
  minCropEdge = 0,
  /** 输出/平台要求的最小宽（已满足比例时，像素不足也会弹裁剪以便放大导出） */
  minOutputWidth = 0,
  /** 输出/平台要求的最小高 */
  minOutputHeight = 0,
} = {}) {
  const resolverRef = useRef(null);
  const [session, setSession] = useState(null);

  const closeSession = useCallback((result) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setSession(null);
    if (!resolver) return;
    if (result) resolver.resolve(result);
    else resolver.reject(new Error('已取消裁剪'));
  }, []);

  const requestCrop = useCallback((file, extras = {}) => {
    return new Promise((resolve, reject) => {
      if (resolverRef.current) {
        resolverRef.current.reject(new Error('已有裁剪任务进行中'));
      }
      resolverRef.current = { resolve, reject };
      setSession({
        file,
        fileName: extras.fileName || file?.name || 'image.jpg',
        ...extras,
      });
    });
  }, []);

  /**
   * 比例与最小尺寸均合格则原样返回；否则弹裁剪框，返回裁剪后的 File。
   * 用户取消时抛错。
   */
  const ensureAspectFile = useCallback(async (file, extras = {}) => {
    if (!file) throw new Error('请选择图片文件');
    const { width, height } = await readImageNaturalSize(file);
    if (!needsFixedAspectCrop({
      width,
      height,
      aspect,
      minOutputWidth,
      minOutputHeight,
    })) {
      return file;
    }

    if (!canCropToMinEdge(width, height, aspect, minCropEdge)) {
      throw new Error(
        `图片过小：无法裁出宽高均 ≥${minCropEdge}px 的 ${aspectLabel} 图（当前 ${width}×${height}），请换更大原图`,
      );
    }

    return requestCrop(file, extras);
  }, [
    aspect,
    aspectLabel,
    minCropEdge,
    minOutputWidth,
    minOutputHeight,
    requestCrop,
  ]);

  const cropModal = (
    <FixedAspectCropModal
      open={Boolean(session)}
      file={session?.file || null}
      fileName={session?.fileName || ''}
      aspect={aspect}
      aspectLabel={aspectLabel}
      title={title}
      hint={hint || `图片比例须为 ${aspectLabel}。请拖动裁剪框选择要保留的区域后确认。`}
      outputWidth={outputWidth}
      outputHeight={outputHeight}
      maxEdge={maxEdge}
      maxBytes={maxBytes}
      minCropEdge={minCropEdge}
      onCancel={() => closeSession(null)}
      onConfirm={(cropped) => closeSession(cropped)}
    />
  );

  return {
    ensureAspectFile,
    requestCrop,
    cropModal,
    cropping: Boolean(session),
  };
}

export default useFixedAspectCropGate;
